import { useParams } from "react-router-dom";
import { useEffect, useState, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store/redux/store/store";
import { UserClaimInput, UserClaimStatus, UserClaimView } from "../../../../models/payroll";
import { getUserInfo } from "../../../../api/General/account";
import { useTranslation } from "react-i18next";
import { getUserClaim, editUserClaim, previewClaimAttachment } from "../../../../api/Payroll/claim";
import { formatDate, formatNumber } from "../../../../utils";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter } from "../../../../components/ui/Dialog";
import Button from "../../../../components/ui/Button";
import { Alert, AlertDescription } from "../../../../components/ui/Alert";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../../../../components/ui/Table";
import { StatusBadge } from "../../../../components/ui/StatusBadge";
import { Spinner } from "../../../../components/ui/Spinner";
import { useToast, ToastRenderer } from "../../../../hooks/useToast";
import { getApiErrorMessage } from '../../../../utils/apiErrors';

const ClaimDetails = () => {
  const { t } = useTranslation('common');
  const { claimId } = useParams(); //same param name as App.tsx
  const claimIdNumber = claimId ? parseInt(claimId, 10) : 0;
  const userId = useSelector((state: RootState) => state.user.userId);
  const role = useSelector((state: RootState) => state.user.role);
  const isManager = useSelector((state: RootState) => state.user.isManager);
  const [claimDetails, setClaimDetails] = useState<UserClaimView | null>(null);
  const [name, setName] = useState("");
  const [reason, setReason] = useState<string>("");
  const [openRejectForm, setRejectForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const { toast, showToast, dismissToast } = useToast();

  const fetchData = useCallback(async () => {
    if (!claimIdNumber) return;
    try {
      setLoading(true);
      const response = await getUserClaim(claimIdNumber);
      if (response.errors.length > 0) {
        showToast(getApiErrorMessage(response, t, t('Failed to load claim')), 'error');
        return;
      }
      const data: UserClaimView = response.payload;
      setClaimDetails(data || null);
      await getUserName();
      if (data?.status === UserClaimStatus.Rejected) setReason(data.managerComment ?? ""); else setReason("");
    } catch (error) {
      console.error("Error fetching claim details:", error);
      showToast(getApiErrorMessage(error, t, t('Failed to load claim')), 'error');
    } finally {
      setLoading(false);
    }
  }, [claimIdNumber, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getUserName = async () => {
    try {
      const result = await getUserInfo();
      if (result.errors.length === 0) {
        setName(result.payload.fullName);
      }
    } catch {
      // Non-blocking — claim details still show without name
    }
  };

  const approveRejectSpecificClaim = async (approve: boolean) => {
    try {
      setLoading(true);
      const updateClaim: UserClaimInput = {
        id: claimIdNumber,
        status: approve ? UserClaimStatus.Approved : UserClaimStatus.Rejected,
        managerComment: reason,
      };
      await editUserClaim(claimIdNumber, updateClaim);
      showToast(approve ? t('Claim approved') : t('Claim rejected'), 'success');
      await fetchData();
    } catch (error: any) {
      console.error('Claim approve/reject failed:', error);
      // CO-575 w1: `error?.message` is undefined on a parsed `Result` body, so this could only
      // ever render "Action failed" — the server's reason for refusing the approval was dropped.
      showToast(getApiErrorMessage(error, t, t('Action failed')), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenROR = () => {
    setReason("");
    setRejectForm(true);
  };
  const handleCloseROR = async () => {
    setRejectForm(false);
  };
  const viewClaimAttachment = async (id: number) => {
    try {
      const blob = await previewClaimAttachment(id);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch {
      showToast(t('Failed to load attachment'), 'error');
    }
  };

  const attachmentLinks = useMemo(() => {
    if (!claimDetails?.userClaimAttachments?.length) return null;
    return (
      <ul className="m-0 ps-4 list-disc">
        {claimDetails.userClaimAttachments.map(att => (
          <li key={att.id}>
            <a href="#" className="text-primary hover:underline" onClick={(e) => { e.preventDefault(); viewClaimAttachment(att.id); }}>{att.fileName}</a>
          </li>
        ))}
      </ul>
    );
  }, [claimDetails]);

  const statusBadge = useMemo(() => {
    if (!claimDetails) return null;
    let variant: 'success' | 'error' | 'warning' | 'info' | 'neutral' = 'neutral';
    switch (claimDetails.status) {
      case UserClaimStatus.Approved: variant = 'success'; break;
      case UserClaimStatus.Rejected: variant = 'error'; break;
      case UserClaimStatus.Pending: variant = 'warning'; break;
      case UserClaimStatus.PendingPayment: variant = 'info'; break;
      case UserClaimStatus.Paid: variant = 'success'; break;
    }
    return <StatusBadge variant={variant} size="sm">{t(claimDetails.status)}</StatusBadge>;
  }, [claimDetails, t]);

  const statusAlert = useMemo(() => {
    if (!claimDetails) return null;
    if (claimDetails.status === UserClaimStatus.Approved || claimDetails.status === UserClaimStatus.PendingPayment || claimDetails.status === UserClaimStatus.Paid) {
      return (
        <Alert variant="success">
          <AlertDescription>
            {t('This request was approved by')} {" "}
            {claimDetails.decidedByName || claimDetails.manager?.fullName || t('Unknown User')} {t('on')} {claimDetails.dateOfApprovalOrRejection ? new Date(claimDetails.dateOfApprovalOrRejection).toDateString() : t('Date not available')}
          </AlertDescription>
        </Alert>
      );
    }
    if (claimDetails.status === UserClaimStatus.Rejected) {
      return (
        <Alert variant="error">
          <AlertDescription>
            {t('This request was rejected by')} {" "}
            {claimDetails.decidedByName || claimDetails.manager?.fullName || t('Unknown User')} {t('on')} {claimDetails.dateOfApprovalOrRejection ? new Date(claimDetails.dateOfApprovalOrRejection).toDateString() : t('Date not available')}
            <br />
            {t('Reason')}: {reason || t('No Reason Available')}
          </AlertDescription>
        </Alert>
      );
    }
    return null;
  }, [claimDetails, reason, t]);

  // Claim approval is HR-owned; Operator (system/subscription role) has no company-data access (CLAUDE.md
  // Role Model). Approvers are HR or the claimant's own manager (not the claimant themselves).
  const canApproveReject = role === 'Hr' || (isManager && userId !== claimDetails?.userId);
  const currencyCode = claimDetails?.currency?.currencyCode || '';
  const amountFormatted = claimDetails ? `${formatNumber(parseFloat(claimDetails.amount || '0'))} ${currencyCode}`.trim() : '-';

  return (
    <div className="relative flex flex-col gap-4 py-4 pe-5">
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/50 z-10">
          <Spinner size="lg" />
        </div>
      )}

      <div className="flex items-start justify-between flex-wrap gap-3">
        <h2 className="text-xl font-semibold text-foreground">{t('Claim No')} : {claimDetails?.id || '-'}</h2>
        <div className="flex items-center gap-2">
          {statusBadge}
          {canApproveReject && (
            <>
              <Button
                variant="success"
                size="sm"
                onClick={() => approveRejectSpecificClaim(true)}
                disabled={!claimDetails || claimDetails.status !== UserClaimStatus.Pending}
              >
                {t('APPROVE')}
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => { handleOpenROR(); setRejectForm(true); }}
                disabled={!claimDetails || claimDetails.status !== UserClaimStatus.Pending}
              >
                {t('REJECT')}
              </Button>
            </>
          )}
        </div>
      </div>

      {statusAlert}

      <div className="rounded-lg border border-border bg-card p-4">
        <div className="flex flex-wrap gap-8">
          <div className="flex flex-col gap-1 min-w-[180px]">
            <span className="text-sm font-semibold text-foreground">{t('Employee Name')}</span>
            <span className="text-sm text-muted-foreground">{name || '-'}</span>
          </div>
          <div className="flex flex-col gap-1 min-w-[180px]">
            <span className="text-sm font-semibold text-foreground">{t('Claim Request Date')}</span>
            <span className="text-sm text-muted-foreground">{claimDetails ? formatDate(new Date(claimDetails.requestDate)) : t('No Date Available')}</span>
          </div>
          <div className="flex flex-col gap-1 min-w-[180px]">
            <span className="text-sm font-semibold text-foreground">{t('Total Claimed Amount')}</span>
            <span className="text-sm text-muted-foreground">{amountFormatted}</span>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('CLAIM REQUEST DATE')}</TableHead>
              <TableHead>{t('CLAIM NUMBER')}</TableHead>
              <TableHead>{t('CLAIM AMOUNT')}</TableHead>
              <TableHead>{t('COMMENTS')}</TableHead>
              <TableHead>{t('ATTACHMENTS')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell>{claimDetails ? formatDate(new Date(claimDetails.requestDate)) : t('No Date Available')}</TableCell>
              <TableCell>{claimDetails?.id || '-'}</TableCell>
              <TableCell>{amountFormatted}</TableCell>
              <TableCell>{claimDetails?.comments || t('No Comment Available')}</TableCell>
              <TableCell>{attachmentLinks || t('No Attachment Available')}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>

      {/* Reject Reason Dialog */}
      <Dialog open={openRejectForm} onOpenChange={(open) => !open && handleCloseROR()}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>{t('REASON OF REJECTION')}</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-foreground">{t('Reason Of Rejection')}</label>
              <textarea
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary disabled:opacity-50"
                rows={4}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseROR}>{t('Cancel')}</Button>
            <Button
              variant="destructive"
              onClick={async () => { await approveRejectSpecificClaim(false); handleCloseROR(); }}
            >
              {t('SUBMIT')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ToastRenderer toast={toast} onDismiss={dismissToast} />
    </div>
  );
};

export default ClaimDetails;
